import React, { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import { getAgentDashboard } from '../services/api';
import AgentCard from './AgentCard';
import AgentDetailPage from './AgentDetailPage';
import './AgentStatusDashboard.css';

const AgentStatusDashboard = ({ embedMode = false }) => {
  const { instance, accounts } = useMsal();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [summaryCollapsed, setSummaryCollapsed] = useState(true); // Collapsed by default
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [sortBy, setSortBy] = useState('status'); // 'status', 'name', 'calls', 'chats', 'login'

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  const fetchDashboardData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const userEmail = accounts[0]?.username || '';
      const data = await getAgentDashboard(userEmail);
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch agent data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAgents = () => {
    if (!dashboardData?.agents) return [];
    
    let filtered = [];
    if (filterStatus === 'all') filtered = [...dashboardData.agents];
    else if (filterStatus === 'online') filtered = dashboardData.agents.filter(a => a.online);
    else if (filterStatus === 'offline') filtered = dashboardData.agents.filter(a => !a.online);
    else {
      filtered = dashboardData.agents.filter(
        a => a.status?.name?.toLowerCase() === filterStatus.toLowerCase()
      );
    }

    // Apply sorting, always pinning the current user's card first
    const userEmail = accounts[0]?.username?.toLowerCase() || '';
    return filtered.sort((a, b) => {
      const aIsUser = a.email?.toLowerCase() === userEmail;
      const bIsUser = b.email?.toLowerCase() === userEmail;
      if (aIsUser && !bIsUser) return -1;
      if (!aIsUser && bIsUser) return 1;

      switch (sortBy) {
        case 'name':
          return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
        
        case 'status':
          // Sort by online first, then by status name
          if (a.online !== b.online) return b.online ? 1 : -1;
          return (a.status?.name || '').localeCompare(b.status?.name || '');
        
        case 'calls':
          return (b.calls_24h || b.call_count || 0) - (a.calls_24h || a.call_count || 0);
        
        case 'chats':
          return (b.chats_24h || b.chat_count || 0) - (a.chats_24h || a.chat_count || 0);
        
        case 'login':
          // Sort by last login time (most recent first)
          const dateA = new Date(a.last_login_time || 0);
          const dateB = new Date(b.last_login_time || 0);
          return dateB - dateA;
        
        default:
          return 0;
      }
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'available': '#10b981',
      'in-call': '#3b82f6',
      'wrap-up': '#8b5cf6',
      'away': '#f59e0b',
      'unavailable': '#ef4444',
      'offline': '#6b7280'
    };
    return statusColors[status?.toLowerCase()] || '#6b7280';
  };

  if (loading && !dashboardData) {
    return (
      <div className="agent-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading agent dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="agent-dashboard">
        <div className="error-container">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => fetchDashboardData()}>Retry</button>
        </div>
      </div>
    );
  }

  const filteredAgents = getFilteredAgents();
  const summary = dashboardData?.summary || {};
  const currentUserEmail = accounts[0]?.username?.toLowerCase() || '';
  const currentUserAgent = dashboardData?.agents?.find(
    a => a.email?.toLowerCase() === currentUserEmail
  );
  const currentUserRoles = currentUserAgent?.roles || [];
  const isAdmin = currentUserRoles.some(r => r.toLowerCase().includes('admin'));
  const isManager = currentUserRoles.some(r => r.toLowerCase().includes('manager') && !r.toLowerCase().includes('data'));
  // Org is decided server-side (RBDCAI-1070); fall back to redbull if not present.
  const currentUserOrg = currentUserAgent?.org || 'redbull';
  const isRbAdmin = isAdmin && currentUserOrg === 'redbull';
  const isDyopathAdmin = isAdmin && currentUserOrg === 'dyopath';
  const isDyopathManager = isManager && currentUserOrg === 'dyopath';

  // If an agent is selected, show detail page
  if (selectedAgent) {
    return (
      <AgentDetailPage
        agent={selectedAgent}
        onClose={() => setSelectedAgent(null)}
        isRbAdmin={isRbAdmin}
        isDyopathAdmin={isDyopathAdmin}
        isDyopathManager={isDyopathManager}
      />
    );
  }

  return (
    <div className={`agent-dashboard ${embedMode ? 'embed-mode' : ''}`}>
      {!embedMode && (
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Agent Status Dashboard</h1>
          <p className="subtitle">Redbull Liberty Support</p>
        </div>
        <div className="header-right">
          {accounts[0] && (
            <div className="user-info">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              <span>{accounts[0].name || accounts[0].username}</span>
            </div>
          )}
          <div className="auto-refresh-toggle">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span>Auto-refresh (10s)</span>
            </label>
          </div>
          <button className="refresh-btn" onClick={() => fetchDashboardData()}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            Refresh
          </button>
          <button className="logout-btn" onClick={() => instance.logoutRedirect()}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
            </svg>
            Logout
          </button>
        </div>
      </div>
      )}

      {/* Summary Stats */}
      <div className="summary-section">
        <button 
          className="summary-toggle-btn"
          onClick={() => setSummaryCollapsed(!summaryCollapsed)}
          aria-label={summaryCollapsed ? "Expand summary" : "Collapse summary"}
        >
          <span className="summary-toggle-text">Summary Statistics</span>
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="currentColor"
            className={`summary-toggle-icon ${summaryCollapsed ? 'collapsed' : ''}`}
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
          </svg>
        </button>
        
        <div className={`summary-stats ${summaryCollapsed ? 'collapsed' : ''}`}>
        
        {/* Main Summary Cards */}
        <div className="stat-card total">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.total || 0}</div>
            <div className="stat-label">Total Agents</div>
          </div>
        </div>

        <div className="stat-card online">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.online || 0}</div>
            <div className="stat-label">Online</div>
          </div>
        </div>

        <div className="stat-card offline">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.offline || 0}</div>
            <div className="stat-label">Offline</div>
          </div>
        </div>

        {summary.status_breakdown && Object.entries(summary.status_breakdown).map(([status, count]) => (
          <div key={status} className="stat-card status-breakdown">
            <div className="stat-icon" style={{ backgroundColor: getStatusColor(status) }}>
              <div className="status-dot"></div>
            </div>
            <div className="stat-content">
              <div className="stat-value">{count}</div>
              <div className="stat-label">{status}</div>
            </div>
          </div>
        ))}

        {/* Charts Section */}
        <div className="chart-card status-distribution-chart">
          <h3 className="chart-title">Status Distribution</h3>
          <div className="donut-chart-container">
            <svg viewBox="0 0 100 100" className="donut-chart">
              {summary.status_breakdown && (() => {
                const total = summary.total || 1;
                let currentAngle = 0;
                return Object.entries(summary.status_breakdown).map(([status, count], index) => {
                  const percentage = (count / total) * 100;
                  const angle = (percentage / 100) * 360;
                  const startAngle = currentAngle;
                  const endAngle = currentAngle + angle;
                  currentAngle = endAngle;
                  
                  const startX = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
                  const startY = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
                  const endX = 50 + 40 * Math.cos((endAngle - 90) * Math.PI / 180);
                  const endY = 50 + 40 * Math.sin((endAngle - 90) * Math.PI / 180);
                  
                  const largeArc = angle > 180 ? 1 : 0;
                  
                  return (
                    <path
                      key={status}
                      d={`M 50 50 L ${startX} ${startY} A 40 40 0 ${largeArc} 1 ${endX} ${endY} Z`}
                      fill={getStatusColor(status)}
                      opacity="0.9"
                      className="donut-slice"
                    >
                      <title>{status}: {count} ({percentage.toFixed(1)}%)</title>
                    </path>
                  );
                });
              })()}
              <circle cx="50" cy="50" r="25" fill="rgba(15, 23, 42, 0.9)" />
              <text x="50" y="50" textAnchor="middle" dy=".3em" fill="#fff" fontSize="16" fontWeight="bold">
                {summary.total}
              </text>
            </svg>
            <div className="chart-legend">
              {summary.status_breakdown && Object.entries(summary.status_breakdown).map(([status, count]) => (
                <div key={status} className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: getStatusColor(status) }}></div>
                  <span className="legend-label">{status}</span>
                  <span className="legend-value">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Agent Productivity Gantt Chart - Admin only */}
        {isAdmin && (
        <div className="chart-card gantt-chart-card">
          <h3 className="chart-title">Agent Productivity (Last 24 Hours)</h3>
          <div className="gantt-chart">
            {dashboardData?.agents?.slice(0, 10).map(agent => {
              const maxCalls = Math.max(...(dashboardData?.agents?.map(a => a.calls_24h || 0) || [1]));
              const maxChats = Math.max(...(dashboardData?.agents?.map(a => a.chats_24h || 0) || [1]));
              const maxTime = 28800; // 8 hours in seconds
              
              const availabilityPercent = ((agent.availability_time_24h || 0) / maxTime) * 100;
              const breakPercent = ((agent.break_time_24h || 0) / maxTime) * 100;
              const callsPercent = ((agent.calls_24h || 0) / (maxCalls || 1)) * 100;
              const chatsPercent = ((agent.chats_24h || 0) / (maxChats || 1)) * 100;
              
              return (
                <div key={agent.id} className="gantt-row">
                  <div className="gantt-agent-name">
                    {agent.first_name} {agent.last_name?.charAt(0)}.
                  </div>
                  <div className="gantt-bars">
                    <div className="gantt-bar-group">
                      <div className="gantt-bar-label">Availability</div>
                      <div className="gantt-bar-track">
                        <div 
                          className="gantt-bar availability"
                          style={{ width: `${availabilityPercent}%` }}
                          title={`${Math.round((agent.availability_time_24h || 0) / 3600)}h available`}
                        >
                          <span className="gantt-bar-text">
                            {Math.round((agent.availability_time_24h || 0) / 3600)}h
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="gantt-bar-group">
                      <div className="gantt-bar-label">Break</div>
                      <div className="gantt-bar-track">
                        <div 
                          className="gantt-bar break"
                          style={{ width: `${breakPercent}%` }}
                          title={`${Math.round((agent.break_time_24h || 0) / 60)}m break`}
                        >
                          <span className="gantt-bar-text">
                            {Math.round((agent.break_time_24h || 0) / 60)}m
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="gantt-bar-group">
                      <div className="gantt-bar-label">Calls</div>
                      <div className="gantt-bar-track">
                        <div 
                          className="gantt-bar calls"
                          style={{ width: `${callsPercent}%` }}
                          title={`${agent.calls_24h || 0} calls`}
                        >
                          <span className="gantt-bar-text">{agent.calls_24h || 0}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="gantt-bar-group">
                      <div className="gantt-bar-label">Chats</div>
                      <div className="gantt-bar-track">
                        <div 
                          className="gantt-bar chats"
                          style={{ width: `${chatsPercent}%` }}
                          title={`${agent.chats_24h || 0} chats`}
                        >
                          <span className="gantt-bar-text">{agent.chats_24h || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {dashboardData?.agents?.length > 10 && (
            <div className="gantt-footer">
              Showing top 10 agents. Click agent cards for full details.
            </div>
          )}
        </div>
        )}

        
        </div>
      </div>

      {/* Filter Tabs with View Toggle and Sort */}
      <div className="filter-section">
        <div className="filter-tabs">
            <button
              className={filterStatus === 'all' ? 'active' : ''}
              onClick={() => setFilterStatus('all')}
            >
              All ({dashboardData?.agents?.length || 0})
            </button>
            <button
              className={filterStatus === 'online' ? 'active' : ''}
              onClick={() => setFilterStatus('online')}
            >
              Online ({summary.online || 0})
            </button>
            <button
              className={filterStatus === 'available' ? 'active' : ''}
              onClick={() => setFilterStatus('available')}
            >
              Available ({summary.status_breakdown?.Available || 0})
            </button>
            <button
              className={filterStatus === 'in-call' ? 'active' : ''}
              onClick={() => setFilterStatus('in-call')}
            >
              In-call ({summary.status_breakdown?.['In-call'] || 0})
            </button>
            <button
              className={filterStatus === 'wrap-up' ? 'active' : ''}
              onClick={() => setFilterStatus('wrap-up')}
            >
              Wrap-up ({summary.status_breakdown?.['Wrap-up'] || 0})
            </button>
            <button
              className={filterStatus === 'unavailable' ? 'active' : ''}
              onClick={() => setFilterStatus('unavailable')}
            >
              Unavailable ({summary.status_breakdown?.Unavailable || 0})
            </button>
            <button
              className={filterStatus === 'offline' ? 'active' : ''}
              onClick={() => setFilterStatus('offline')}
            >
              Offline ({summary.offline || 0})
            </button>
          </div>
        
        <div className="sort-dropdown">
          <label htmlFor="sort-select">Sort by:</label>
          <select 
            id="sort-select"
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="status">Status</option>
            <option value="name">Name (A-Z)</option>
            <option value="calls">Calls (High to Low)</option>
            <option value="chats">Chats (High to Low)</option>
            <option value="login">Last Login (Recent First)</option>
          </select>
        </div>
        
        <div className="view-mode-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 3H3v7h7V3zM21 3h-7v7h7V3zM21 14h-7v7h7v-7zM10 14H3v7h7v-7z" />
              </svg>
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
              </svg>
            </button>
          </div>
      </div>

      {/* Agent Cards Grid/List */}
      <div className={`agents-container ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
        {filteredAgents.length === 0 ? (
          <div className="no-agents">
            <p>No agents found with the selected filter.</p>
          </div>
        ) : (
          filteredAgents.map(agent => {
            const isCurrentUser = agent.email?.toLowerCase() === currentUserEmail;
            let canClick;
            if (isRbAdmin) {
              canClick = true;
            } else if (isDyopathAdmin || isDyopathManager) {
              canClick = isCurrentUser || agent.org === 'dyopath';
            } else {
              canClick = isCurrentUser;
            }
            return (
              <AgentCard 
                key={agent.id} 
                agent={agent} 
                viewMode={viewMode}
                isCurrentUser={isCurrentUser}
                onClick={canClick ? setSelectedAgent : null}
                onStatusChange={() => fetchDashboardData()}
              />
            );
          })
        )}
      </div>

    </div>
  );
};

export default AgentStatusDashboard;

